import { Fragment, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import {
  formatAmountWithCommas,
  FormatCurrencyForDisplay,
  PageStatus,
  SkeletonBase,
  SkeletonLoader,
} from '@unified-checkout/design-system';
import { localizeAmountWithCurrency } from 'libs/design-system/src/utils/localizeAmountWithCurrency';
import { CircleExclamation } from '@unified-checkout/design-system/icons/index';

import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { useLangStore } from '@app/dashboard/store/lang';
import { InfoCard } from '@app/dashboard/components/inner_layout/Cards/InfoCard';
import { convertCentsToMajorUnit } from '@app/dashboard/utils/convertAmounts';
import { formatToLocalizedDateTime } from '@app/dashboard/utils/dateFormats';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { getSingleChargeBackDetails } from '@app/dashboard/services/inner_layout/chargebacks';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';

import Header from './Header';

import styles from './SingleChargeBacks.module.scss';

const EMPTY_VALUE = '-';

const SingleChargeBacks = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { lang } = useLangStore();
  const screenWidth = useScreenWidth();

  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const {
    isError,
    data: response,
    isFetching,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_SINGLE_CHARGE_BACK_DETAILS, id, lang],
    queryFn: () => getSingleChargeBackDetails(id as string),
    enabled: !!id,
    refetchOnMount: 'always',
  });

  const {
    amount_cents: amount,
    currency,
    chargeback_status: status,
    created_at,
    order,
    vat_cents,
    accept_fees_cents,
    parent_transaction_id,
    integration_id,
  } = response?.data || {};

  const currencyFormatter = (amount: number) => {
    return localizeAmountWithCurrency({
      lang,
      amount: formatAmountWithCommas({ amount, currency }),
      currency,
    });
  };

  const detailsBoxRenderer = ({
    box = undefined,
    height,
    noOfRows,
    key,
  }: {
    box?: ReactNode;
    height: string;
    noOfRows: number;
    key: number | string;
  }): ReactNode => {
    return isFetching ? (
      <SkeletonLoader
        type="bordered_box"
        noOfRows={noOfRows}
        height={height}
        key={key}
      />
    ) : (
      box && <Fragment key={key}>{box}</Fragment>
    );
  };

  const allBoxes = [
    detailsBoxRenderer({
      box: (
        <InfoCard
          title={t('innerLayout.chargeBacks.singleChargeBacks.title')}
          pairsList={[
            {
              label: t(
                'innerLayout.chargeBacks.singleChargeBacks.details.transactionId'
              ),
              value: id || EMPTY_VALUE,
            },
            {
              label: t(
                'innerLayout.chargeBacks.singleChargeBacks.details.orderId'
              ),
              value: order?.id?.toString() || EMPTY_VALUE,
            },
            {
              label: t(
                'innerLayout.chargeBacks.singleChargeBacks.details.vatCents'
              ),
              value: vat_cents
                ? currencyFormatter(
                    convertCentsToMajorUnit(vat_cents, currency)
                  )
                : EMPTY_VALUE,
            },
            {
              label: t(
                'innerLayout.chargeBacks.singleChargeBacks.details.acceptFeesCents'
              ),
              value: accept_fees_cents
                ? currencyFormatter(
                    convertCentsToMajorUnit(accept_fees_cents, currency)
                  )
                : EMPTY_VALUE,
            },

            {
              label: t(
                'innerLayout.chargeBacks.singleChargeBacks.details.parentTransactionId'
              ),
              value: parent_transaction_id?.toString() || EMPTY_VALUE,
            },
            {
              label: t(
                'innerLayout.chargeBacks.singleChargeBacks.details.createdAt'
              ),
              value: created_at
                ? formatToLocalizedDateTime(created_at, lang)
                : EMPTY_VALUE,
            },
            {
              label: t(
                'innerLayout.chargeBacks.singleChargeBacks.details.integrationId'
              ),
              value: integration_id?.toString() || EMPTY_VALUE,
            },
          ]}
          noOfColumns={2}
        />
      ),
      noOfRows: 1,
      height: '24rem',
      key: t('innerLayout.chargeBacks.singleChargeBacks.title'),
    }),
  ];

  // Filter out empty boxes
  const filteredBoxes = allBoxes.filter(Boolean);

  // Split the boxes into two columns
  const col1Boxes = filteredBoxes.filter((_, index) => index % 2 === 0); // Even-indexed items
  const col2Boxes = filteredBoxes.filter((_, index) => index % 2 !== 0); // Odd-indexed items

  const content = (
    <>
      {isMobile ? (
        <>
          {col1Boxes}
          {col2Boxes}
        </>
      ) : (
        [col1Boxes, col2Boxes].map((colContent, index) => (
          <div
            className={styles['single-chargeBacks__content-col']}
            key={`single-chargeBacks-col-${index}`}
          >
            {colContent}
          </div>
        ))
      )}
    </>
  );

  return isError ? (
    <PageStatus
      status="error"
      title={t('innerLayout.errors.smthWentWrong')}
      description={t('innerLayout.errors.dataRetrievalErr')}
      icon={CircleExclamation}
    />
  ) : (
    <div className={styles['single-chargeBacks']}>
      {isFetching ? (
        <SkeletonBase height="3.75rem" />
      ) : (
        <Header
          status={status}
          amount={convertCentsToMajorUnit(amount || 0, currency)}
          created_at={created_at || ''}
          currencyFormatter={currencyFormatter}
        />
      )}
      {isFetching ? (
        <SkeletonLoader
          type="bordered_box"
          noOfRows={5}
          height="400px"
          key="single-chargeBacks-skeleton"
        />
      ) : (
        <div className={styles['single-chargeBacks__content']}>{content}</div>
      )}
    </div>
  );
};

export default SingleChargeBacks;
